import { characterTones, knownPartCuts, unambiguousCuts, suggestCuts, suggestNestedCuts, type PartTone, type SplitHints } from '@/utils/splitSuggestion'

export type RunType = 'L' | 'D' | 'S' | 'O'

export interface TokenRun {
  type: RunType
  text: string
}

export interface SplitGroupDraft {
  id: string
  patternKey: string
  label: string
  representative: string
  members: string[]
  outliers: string[]
}

export interface PartRange {
  text: string
  start: number
}

const SEP_ONLY = /^[\s_./\\-]+$/

function stripEdgeSep(text: string): string {
  return text.replace(/^[\s_./\\-]+|[\s_./\\-]+$/g, '')
}

export function shopItemKey(item: { token: string; field: string }): string {
  return `${item.token}|${item.field}`
}

export function shopRowsForDialogToken<T extends { token: string; display?: string | null; field: string }>(
  rows: T[],
  token: string,
): T[] {
  return rows.filter((item) => (item.display || item.token) === token || item.token === token)
}

export function mergeSplitQueue<T>(
  queued: T[],
  pending: T[],
  revoke: string[] | undefined,
  keysOf: (row: T) => string[],
): T[] {
  const drop = new Set(revoke || [])
  for (const row of pending) {
    for (const key of keysOf(row)) drop.add(key)
  }
  return [...queued.filter((row) => keysOf(row).every((key) => !drop.has(key))), ...pending]
}

export function canSplitToken(token: string): boolean {
  if (!token || token.length < 2) return false
  if (/^\d{1,2}:\d{2}[-–]?\d{1,2}:\d{2}$/.test(token)) return false
  if (/^[A-Za-z]$/.test(token)) return false
  return true
}

export function tokenRuns(token: string): TokenRun[] {
  const runs: TokenRun[] = []
  for (const ch of token) {
    const type: RunType = /[A-Za-zÄÖÜäöüß]/.test(ch)
      ? 'L'
      : /\d/.test(ch)
        ? 'D'
        : /[-_./\\]/.test(ch)
          ? 'S'
          : 'O'
    const last = runs[runs.length - 1]
    if (last && last.type === type) last.text += ch
    else runs.push({ type, text: ch })
  }
  return runs
}

export function patternKey(token: string): string {
  return tokenRuns(token).map((run) => run.type).join('')
}

export function runLengthKey(token: string): string {
  return tokenRuns(token).map((run) => `${run.type}${run.text.length}`).join('')
}

export function patternLabel(key: string, sample: string): string {
  if (key.includes('S')) {
    if (sample.includes('/')) return 'Slash'
    if (sample.includes('-')) return 'Bindestrich'
    if (sample.includes('_')) return 'Unterstrich'
    if (sample.includes('.')) return 'Punkt'
    return 'Mit Trennzeichen'
  }
  if (key === 'LDL') return 'Buchstaben + Ziffern + Suffix'
  if (key === 'LD') return 'Buchstaben + Ziffern'
  return 'Gleiche Struktur'
}

export function structuralCuts(token: string): number[] {
  const cuts: number[] = []
  for (let index = 0; index < token.length; index++) {
    if (/[\s/._-]/.test(token[index] || '')) cuts.push(index)
  }
  return cuts.filter((cut) => cut > 0 && cut < token.length)
}

export interface AssignedSplit {
  parts: string[]
  fields: string[]
  subjectIds: Array<string | null>
}

function fieldOfTone(tone: PartTone): string | null {
  if (tone === 'klass') return 'class_name'
  if (tone === 'room') return 'room'
  if (tone === 'subject') return 'subject'
  if (tone === 'ignore') return 'ignore'
  return null
}

function subjectIdFor(part: string, hints: SplitHints): string | null {
  const key = part.trim().toLowerCase()
  const ids = [
    ...new Set(
      (hints.subjectRefs || [])
        .filter((ref) => ref.labels.some((label) => label.trim().toLowerCase() === key))
        .map((ref) => ref.id)
        .filter(Boolean),
    ),
  ]
  return ids.length === 1 ? ids[0]! : null
}

function fieldsFor(token: string, cuts: number[], hints: SplitHints, allowUnknown: boolean): AssignedSplit | null {
  const parts = splitAtCuts(token, cuts)
  if (parts.length < 2) return null
  if (parts.join('').length < token.replace(/[\s.\-_/]+/g, '').length) return null
  const fields: string[] = []
  const subjectIds: Array<string | null> = []
  let known = 0
  for (const part of parts) {
    const tone = characterTones(part, [], hints).find((item) => item) ?? null
    const field = fieldOfTone(tone)
    if (field === 'subject') {
      const id = subjectIdFor(part, hints)
      if (!id) {
        if (!allowUnknown) return null
        fields.push('')
        subjectIds.push(null)
        continue
      }
      known += 1
      fields.push('subject')
      subjectIds.push(id)
      continue
    }
    if (!field) {
      if (!allowUnknown) return null
      fields.push('')
      subjectIds.push(null)
      continue
    }
    known += 1
    fields.push(field)
    subjectIds.push(null)
  }
  if (known === 0) return null
  return { parts, fields, subjectIds }
}

export function assignedSplit(token: string, hints: SplitHints): AssignedSplit | null {
  const strictCuts = unambiguousCuts(token, hints)
  if (strictCuts?.length) {
    const full = fieldsFor(token, strictCuts, hints, false)
    if (full) return full
  }
  const loose = knownPartCuts(token, hints)
  if (!loose?.length) return null
  return fieldsFor(token, loose, hints, true)
}

export function proposalCuts(token: string, hints?: SplitHints): number[] {
  return suggestCuts(token, hints)
}

export function defaultCuts(token: string, hints?: SplitHints): number[] {
  return suggestCuts(token, hints)
}

export function defaultNestedCuts(part: string, hints?: SplitHints): number[] {
  return suggestNestedCuts(part, hints)
}

export function normalizeCuts(token: string, cuts: number[]): number[] {
  return [...new Set(cuts)].filter((cut) => cut > 0 && cut < token.length).sort((a, b) => a - b)
}

function cleanParts(parts: string[]): string[] {
  return parts
    .map((part) => stripEdgeSep(part))
    .filter((part) => part.length > 0 && !SEP_ONLY.test(part))
}

export function mergeTinyParts(parts: string[]): string[] {
  if (parts.length < 2) return parts
  const src = [...parts]
  const out: string[] = []
  for (let i = 0; i < src.length; i++) {
    const part = src[i]!
    if (part.length !== 1) {
      out.push(part)
      continue
    }
    if (i + 1 < src.length) {
      src[i + 1] = part + src[i + 1]!
      continue
    }
    if (out.length > 0) out[out.length - 1] += part
    else out.push(part)
  }
  return out
}

export function splitAtCuts(token: string, cuts: number[]): string[] {
  const sorted = normalizeCuts(token, cuts)
  if (sorted.length === 0) return mergeTinyParts(cleanParts([token]))
  const parts: string[] = []
  let start = 0
  for (const cut of sorted) {
    parts.push(token.slice(start, cut))
    start = cut
  }
  parts.push(token.slice(start))
  return cleanParts(parts)
}

export function partRanges(token: string, cuts: number[]): PartRange[] {
  const sorted = normalizeCuts(token, cuts)
  const ends = [...sorted, token.length]
  const ranges: PartRange[] = []
  let start = 0
  for (const end of ends) {
    const raw = token.slice(start, end)
    const trimmed = stripEdgeSep(raw)
    if (trimmed) {
      const offset = raw.indexOf(trimmed)
      ranges.push({ text: trimmed, start: start + Math.max(0, offset) })
    }
    start = end
  }
  return ranges
}

export function withNestedCuts(
  token: string,
  primaryCuts: number[],
  nested: { partIndex: number; cuts: number[] } | null,
): number[] {
  if (!nested) return normalizeCuts(token, primaryCuts)
  const ranges = partRanges(token, primaryCuts)
  const range = ranges[nested.partIndex]
  if (!range) return normalizeCuts(token, primaryCuts)
  const extra = nested.cuts.map((cut) => range.start + cut)
  return normalizeCuts(token, [...primaryCuts, ...extra])
}

export function buildSplitGroups(tokens: string[]): SplitGroupDraft[] {
  const unique = [...new Set(tokens.filter(canSplitToken))]
  const byPattern = new Map<string, string[]>()
  for (const token of unique) {
    const key = patternKey(token)
    const list = byPattern.get(key) || []
    list.push(token)
    byPattern.set(key, list)
  }
  const groups: SplitGroupDraft[] = []
  let index = 0
  for (const [key, list] of byPattern) {
    const byRun = new Map<string, string[]>()
    for (const token of list) {
      const runKey = runLengthKey(token)
      const bucket = byRun.get(runKey) || []
      bucket.push(token)
      byRun.set(runKey, bucket)
    }
    let majority: string[] = []
    for (const bucket of byRun.values()) {
      if (bucket.length > majority.length) majority = bucket
    }
    const majoritySet = new Set(majority)
    const outliers = list.filter((token) => !majoritySet.has(token))
    const representative = majority[0] || list[0]!
    groups.push({
      id: `g${index++}`,
      patternKey: key,
      label: patternLabel(key, representative),
      representative,
      members: majority.length ? majority : [representative],
      outliers,
    })
  }
  return groups
}
